import { parseOptionalClinicalMeasurement } from './clinical-measurement';

export type WoundMeasurementState = 'MEASURED' | 'NOT_MEASURABLE' | 'NOT_RECORDED';
export type WoundMeasurementEvidence = { state: WoundMeasurementState; reason: string | null };
export type WoundDimensions = {
  length?: unknown;
  width?: unknown;
  depth?: unknown;
  area?: unknown;
};

export class WoundMeasurementValidationError extends Error {
  constructor(
    message: string,
    readonly field: keyof WoundDimensions | 'measurementState' | 'measurementReason',
  ) {
    super(message);
    this.name = 'WoundMeasurementValidationError';
  }
}

/** New evidence only. Historical absence is not evidence of a deliberate choice. */
export function validateWoundMeasurementEvidence(
  dimensions: WoundDimensions,
  state?: unknown,
  reason?: unknown,
): WoundMeasurementEvidence {
  const hasMeasurement = (['length', 'width', 'depth', 'area'] as const)
    .map((field) => {
      try {
        return parseOptionalClinicalMeasurement(dimensions[field], field);
      } catch {
        throw new WoundMeasurementValidationError(
          `${field} must be a number of zero or more.`,
          field,
        );
      }
    })
    .some((value) => value !== null);
  const selected =
    state == null || state === '' ? (hasMeasurement ? 'MEASURED' : 'NOT_RECORDED') : state;
  if (!['MEASURED', 'NOT_MEASURABLE', 'NOT_RECORDED'].includes(selected as string))
    throw new WoundMeasurementValidationError(
      'Choose a valid measurement state.',
      'measurementState',
    );
  if (reason != null && typeof reason !== 'string')
    throw new WoundMeasurementValidationError(
      'Measurement reason must be text.',
      'measurementReason',
    );
  const explanation = typeof reason === 'string' ? reason.trim() || null : null;
  if (selected === 'MEASURED' && !hasMeasurement)
    throw new WoundMeasurementValidationError(
      'Enter at least one measurement, or select not recorded or not measurable.',
      'length',
    );
  if (selected !== 'MEASURED' && hasMeasurement)
    throw new WoundMeasurementValidationError(
      'Remove measurements before selecting not recorded or not measurable.',
      (['length', 'width', 'depth', 'area'] as const).find(
        (field) => parseOptionalClinicalMeasurement(dimensions[field], field) !== null,
      ) ?? 'length',
    );
  if (selected === 'NOT_MEASURABLE' && !explanation)
    throw new WoundMeasurementValidationError(
      'Explain why the wound could not be measured.',
      'measurementReason',
    );
  if (selected !== 'NOT_MEASURABLE' && explanation)
    throw new WoundMeasurementValidationError(
      'A measurement reason applies only when the wound is not measurable.',
      'measurementReason',
    );
  return { state: selected as WoundMeasurementState, reason: explanation };
}

/** Unknown/malformed legacy evidence stays unknown and is never silently repaired. */
export function readWoundMeasurementEvidence(
  metadata: unknown,
  dimensions: WoundDimensions,
): WoundMeasurementEvidence | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null;
  const evidence = (metadata as Record<string, unknown>).measurement;
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) return null;
  const row = evidence as Record<string, unknown>;
  if (row.state == null || row.state === '') return null;
  try {
    return validateWoundMeasurementEvidence(dimensions, row.state, row.reason);
  } catch {
    return null;
  }
}

export function woundMeasurementStateLabel(evidence: WoundMeasurementEvidence | null): string {
  if (!evidence) return 'Measurement state not recorded';
  if (evidence.state === 'NOT_MEASURABLE') return `Not measurable · ${evidence.reason}`;
  return evidence.state === 'MEASURED' ? 'Measured' : 'Measurements not recorded';
}

/** Flag anomalous historical values without repairing the source record. */
export function woundMeasurementsNeedReview(
  metadata: unknown,
  dimensions: WoundDimensions,
): boolean {
  try {
    for (const field of ['length', 'width', 'depth', 'area'] as const)
      parseOptionalClinicalMeasurement(dimensions[field], field);
  } catch {
    return true;
  }
  return Boolean(
    metadata &&
    typeof metadata === 'object' &&
    !Array.isArray(metadata) &&
    Object.prototype.hasOwnProperty.call(metadata, 'measurement') &&
    !readWoundMeasurementEvidence(metadata, dimensions),
  );
}

export type InitialWoundMeasurementEvidence = WoundMeasurementEvidence & {
  length: number | null;
  width: number | null;
  depth: number | null;
  area: number | null;
  recordedAt: string;
  recordedBy: string;
};

/** Read the immutable snapshot, never infer it from the wound's current values. */
export function readInitialWoundMeasurementEvidence(
  value: unknown,
): InitialWoundMeasurementEvidence | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const evidence = readWoundMeasurementEvidence({ measurement: row }, row);
  const recordedAt = row.recorded_at ?? row.recordedAt;
  const recordedBy = row.recorded_by ?? row.recordedBy;
  if (
    !evidence ||
    typeof recordedAt !== 'string' ||
    !Number.isFinite(Date.parse(recordedAt)) ||
    typeof recordedBy !== 'string' ||
    !recordedBy ||
    (row.length_unit ?? row.lengthUnit) !== 'cm' ||
    (row.area_unit ?? row.areaUnit) !== 'cm2'
  )
    return null;
  return {
    ...evidence,
    length: parseOptionalClinicalMeasurement(row.length, 'length'),
    width: parseOptionalClinicalMeasurement(row.width, 'width'),
    depth: parseOptionalClinicalMeasurement(row.depth, 'depth'),
    area: parseOptionalClinicalMeasurement(row.area, 'area'),
    recordedAt,
    recordedBy,
  };
}

export function initialWoundMeasurementLabel(value: unknown): string {
  if (value == null) return 'Initial measurement evidence not recorded';
  const evidence = readInitialWoundMeasurementEvidence(value);
  if (!evidence) return 'Initial measurement evidence needs review';
  const dimensions = (
    [
      ['Length', evidence.length, 'cm'],
      ['Width', evidence.width, 'cm'],
      ['Depth', evidence.depth, 'cm'],
      ['Area', evidence.area, 'cm²'],
    ] as const
  )
    .filter(([, amount]) => amount !== null)
    .map(([label, amount, unit]) => `${label} ${amount} ${unit}`);
  return [woundMeasurementStateLabel(evidence), ...dimensions].join(' · ');
}
